import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const ThemeContext = createContext();

export const useThemeMode = () => useContext(ThemeContext);

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem('themeMode') || 'light');

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: '#16325b',
        light: '#355989',
        dark: '#0a1d38'
      },
      secondary: {
        main: '#0f766e',
        light: '#279a8f',
        dark: '#0a554e'
      },
      success: { main: '#22c55e' },
      info: { main: '#2563eb' },
      warning: { main: '#d97706' },
      error: { main: '#dc2626' },
      background: mode === 'dark'
        ? { default: '#020617', paper: '#0b1728' }
        : { default: '#eef3f9', paper: '#fbfdff' },
      text: mode === 'dark'
        ? { primary: '#e5eefb', secondary: '#9aa9c0' }
        : { primary: '#10213a', secondary: '#61748f' },
      surface: mode === 'dark'
        ? {
            raised: 'linear-gradient(135deg, rgba(11,23,40,0.98) 0%, rgba(8,18,33,0.98) 100%)',
            governance: 'linear-gradient(135deg, rgba(45,31,14,0.72) 0%, rgba(11,23,40,0.98) 100%)',
            subtle: 'rgba(15, 29, 49, 0.82)'
          }
        : {
            raised: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,250,254,0.97) 100%)',
            governance: 'linear-gradient(135deg, rgba(255,248,235,0.96) 0%, rgba(255,255,255,0.99) 100%)',
            subtle: 'rgba(255,255,255,0.76)'
          }
    },
    status: {
      attention: mode === 'dark' ? '#f6ad55' : '#b45309',
      review: mode === 'dark' ? '#93c5fd' : '#1d4ed8',
      neutral: mode === 'dark' ? '#9aa9c0' : '#61748f'
    },
    typography: {
      fontFamily: '"Avenir Next", "PingFang SC", "Noto Sans SC", sans-serif',
      h3: { fontWeight: 800, letterSpacing: '-0.05em' },
      h4: { fontWeight: 800, letterSpacing: '-0.04em' },
      h5: { fontWeight: 700, letterSpacing: '-0.025em' },
      h6: { fontWeight: 700 },
      subtitle1: { fontWeight: 600 },
      button: { fontWeight: 700 },
      overline: {
        fontFamily: '"SFMono-Regular", "Menlo", monospace',
        fontWeight: 700,
        letterSpacing: '0.14em'
      },
      caption: {
        letterSpacing: '0.01em'
      }
    },
    shape: {
      borderRadius: 18
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: mode === 'dark'
              ? 'radial-gradient(circle at top left, rgba(34,197,94,0.08), transparent 24%), radial-gradient(circle at top right, rgba(53,89,137,0.14), transparent 28%), linear-gradient(180deg, #04101f 0%, #020617 100%)'
              : 'radial-gradient(circle at top left, rgba(22,50,91,0.08), transparent 28%), radial-gradient(circle at top right, rgba(34,197,94,0.08), transparent 24%), linear-gradient(180deg, #f6f9fc 0%, #eef3f9 100%)'
          },
          '*': {
            boxSizing: 'border-box'
          },
          'button:focus-visible, a:focus-visible, [role="button"]:focus-visible': {
            outline: `3px solid ${mode === 'dark' ? '#93c5fd' : '#2563eb'}`,
            outlineOffset: 2
          },
          '@media (prefers-reduced-motion: reduce)': {
            'html': { scrollBehavior: 'auto' },
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important'
            }
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 24,
            border: mode === 'dark' ? '1px solid rgba(159,176,199,0.12)' : '1px solid rgba(188,204,220,0.55)',
            boxShadow: mode === 'dark'
              ? '0 16px 40px rgba(2, 8, 23, 0.28)'
              : '0 24px 60px rgba(16, 33, 58, 0.08)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 20px 44px rgba(2, 8, 23, 0.34)'
                : '0 28px 64px rgba(16, 33, 58, 0.13)',
              borderColor: mode === 'dark' ? 'rgba(95,141,255,0.22)' : 'rgba(31,94,255,0.18)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none'
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 18,
            border: mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15, 23, 42, 0.07)'
          },
          standardInfo: {
            backgroundColor: mode === 'dark' ? 'rgba(53, 89, 137, 0.18)' : 'rgba(22, 50, 91, 0.08)'
          },
          standardSuccess: {
            backgroundColor: mode === 'dark' ? 'rgba(34, 197, 94, 0.14)' : 'rgba(34, 197, 94, 0.1)'
          },
          standardWarning: {
            backgroundColor: mode === 'dark' ? 'rgba(217, 119, 6, 0.14)' : 'rgba(217, 119, 6, 0.1)'
          },
          standardError: {
            backgroundColor: mode === 'dark' ? 'rgba(214, 69, 69, 0.14)' : 'rgba(214, 69, 69, 0.08)'
          },
          message: {
            fontWeight: 500
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(16px)',
            backgroundImage: 'none',
            borderBottom: mode === 'dark' ? '1px solid rgba(148, 163, 184, 0.12)' : '1px solid rgba(199, 210, 223, 0.72)'
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: mode === 'dark'
              ? 'linear-gradient(180deg, rgba(8, 17, 32, 0.98) 0%, rgba(5, 12, 23, 0.98) 100%)'
              : 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,247,252,0.98) 100%)'
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 14,
            padding: '10px 18px',
            minHeight: 44,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
            '&:active': {
              transform: 'scale(0.98)'
            }
          },
          contained: {
            boxShadow: '0 14px 28px rgba(16, 33, 58, 0.18)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: '0 18px 32px rgba(16, 33, 58, 0.26)'
            }
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
              backgroundColor: mode === 'dark' ? 'rgba(95,141,255,0.08)' : 'rgba(31,94,255,0.05)'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 16,
              backgroundColor: mode === 'dark' ? 'rgba(16,26,44,0.85)' : 'rgba(255,255,255,0.92)',
              '& fieldset': {
                borderColor: mode === 'dark' ? 'rgba(159,176,199,0.18)' : 'rgba(188,204,220,0.8)'
              },
              '&:hover fieldset': {
                borderColor: mode === 'dark' ? 'rgba(95,141,255,0.5)' : 'rgba(31,94,255,0.45)'
              },
              '&.Mui-focused fieldset': {
                borderWidth: 1.5
              }
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 700,
            borderRadius: 999
          }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44
          }
        }
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            minHeight: 44
          }
        }
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontWeight: 800
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 700,
            color: mode === 'dark' ? '#d6e0ef' : '#24364d',
            borderBottomColor: mode === 'dark' ? 'rgba(159,176,199,0.12)' : 'rgba(188,204,220,0.8)',
            backgroundColor: mode === 'dark' ? 'rgba(7, 17, 31, 0.94)' : 'rgba(247, 250, 254, 0.96)',
            backdropFilter: 'blur(12px)'
          },
          body: {
            borderBottomColor: mode === 'dark' ? 'rgba(159,176,199,0.1)' : 'rgba(226,234,244,1)'
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.18s ease'
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 44
          },
          indicator: {
            height: 3,
            borderRadius: 999
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 700,
            minHeight: 44
          }
        }
      }
    }
  }), [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
